import { useEffect, useLayoutEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { X, ArrowLeft, ArrowRight, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const STEPS = [
  { title: "Command Center", body: "This is Today. Due emails, LinkedIn tasks, and mailbox errors land here.", targets: ['[data-testid="today-list"]', '[data-testid="nav-dashboard"]'] },
  { title: "Operator", body: "Tell it what you sell. It drafts the offer, strategy, and sequence. You still approve the first emails.", targets: ['[data-testid="operator-toggle"]', '[data-testid="dash-operator-btn"]'] },
  { title: "Offer \u2192 Strategy", body: "What you sell, who buys, proof you are allowed to say. Empty proof = no made-up numbers.", targets: ['[data-testid="nav-offer"]'] },
  { title: "Leads", body: "Find or import. Public-sector rows need a source URL before live send. Sales Navigator is a CSV you export, not a scrape.", targets: ['[data-testid="nav-leads"]'] },
  { title: "Campaigns", body: "Approve the first-touch template once (or the 5 samples). Follow-ups send on the delay. LinkedIn is a task you send yourself.", targets: ['[data-testid="nav-campaigns"]'] },
  { title: "Inbox + Pipeline", body: "Replies stop the sequence. Interested moves the deal. You are the sender, not DealsOps.", targets: ['[data-testid="nav-inbox"]'] },
  { title: "Sending", body: "Connect Google or Outlook. Test the mailbox. If it says Error, reconnect before a campaign runs.", targets: ['[data-testid="nav-compliance"]'] },
];

const findTarget = (targets) => {
  for (const sel of targets) { const el = document.querySelector(sel); if (el) return el; }
  return null;
};

export function TourOverlay() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;

  const flagKey = user && user.id ? `dealsops_tour_shown_${user.id}` : null;

  // Auto-open once per user (server 'pending' + not shown this browser).
  useEffect(() => {
    if (!user || user === false || !flagKey) return;
    if (!pathname.startsWith("/app")) return;
    const status = user.tour_status;
    const shown = localStorage.getItem(flagKey);
    if ((!status || status === "pending") && !shown) {
      localStorage.setItem(flagKey, "1");
      setIdx(0);
      setOpen(true);
    }
  }, [user, flagKey, pathname]);

  // Replay from Profile.
  useEffect(() => {
    const onReplay = () => { setIdx(0); setOpen(true); };
    window.addEventListener("dealsops:replay-tour", onReplay);
    return () => window.removeEventListener("dealsops:replay-tour", onReplay);
  }, []);

  const measure = useCallback(() => {
    if (isMobile) { setRect(null); return; }
    const el = findTarget(STEPS[idx].targets);
    if (!el) { setRect(null); return; }
    try { el.scrollIntoView({ block: "nearest", inline: "nearest" }); } catch { /* noop */ }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom });
  }, [idx, isMobile]);

  useLayoutEffect(() => {
    if (!open) return;
    measure();
    const t = setTimeout(measure, 60);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [open, idx, measure]);

  if (!open) return null;

  const step = STEPS[idx];
  const last = idx === STEPS.length - 1;

  const persist = (status) => { api.post("/me/tour", { status }).catch(() => {}); };
  const close = (status) => { persist(status); setOpen(false); };
  const next = () => { if (last) close("done"); else setIdx((i) => i + 1); };
  const back = () => setIdx((i) => Math.max(0, i - 1));

  // Card placement (desktop): to the right of the target, else below, else centered.
  let cardStyle = {};
  const CARD_W = 340, CARD_H = 210, PAD = 16;
  const vw = window.innerWidth, vh = window.innerHeight;
  if (!isMobile && rect) {
    if (rect.right + CARD_W + PAD < vw) {
      cardStyle = { left: rect.right + PAD, top: Math.min(Math.max(rect.top, PAD), vh - CARD_H - PAD) };
    } else if (rect.bottom + CARD_H + PAD < vh) {
      cardStyle = { top: rect.bottom + PAD, left: Math.min(Math.max(rect.left, PAD), vw - CARD_W - PAD) };
    } else {
      cardStyle = { left: (vw - CARD_W) / 2, top: (vh - CARD_H) / 2 };
    }
  }

  const CardInner = (
    <div className="flex flex-col" data-testid="tour-card">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-amber-500"><Compass className="h-3.5 w-3.5" /> How DealsOps works</div>
        <button onClick={() => close("dismissed")} className="text-muted-foreground hover:text-foreground" data-testid="tour-skip-x"><X className="h-4 w-4" /></button>
      </div>
      <h3 className="font-display text-lg font-semibold tracking-tight" data-testid="tour-title">{step.title}</h3>
      <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed" data-testid="tour-body">{step.body}</p>
      <div className="flex items-center gap-1.5 mt-4">
        {STEPS.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-amber-500" : "w-1.5 bg-border"}`} data-testid={`tour-dot-${i}`} />)}
      </div>
      <div className="flex items-center justify-between mt-4">
        <button onClick={() => close("dismissed")} className="text-xs text-muted-foreground hover:text-foreground" data-testid="tour-skip">Skip &amp; don't show again</button>
        <div className="flex items-center gap-2">
          {idx > 0 && <Button size="sm" variant="ghost" className="h-8 rounded-md" onClick={back} data-testid="tour-back"><ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back</Button>}
          <Button size="sm" className="h-8 rounded-md bg-amber-500 text-black hover:bg-amber-400" onClick={next} data-testid="tour-next">{last ? "Done" : <>Next <ArrowRight className="h-3.5 w-3.5 ml-1" /></>}</Button>
        </div>
      </div>
    </div>
  );

  if (isMobile || !rect) {
    // Mobile: stacked bottom sheet. Also the fallback when no target is found.
    return (
      <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="tour-overlay">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
        <div className="relative z-[62] w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl border border-border/70 bg-card p-5 shadow-2xl m-0 sm:m-4">
          {CardInner}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60]" data-testid="tour-overlay">
      <div className="absolute inset-0" style={{ zIndex: 60 }} onClick={(e) => e.stopPropagation()} />
      <div
        data-testid="tour-spotlight"
        style={{ position: "fixed", left: rect.left - 6, top: rect.top - 6, width: rect.width + 12, height: rect.height + 12,
          borderRadius: 12, boxShadow: "0 0 0 9999px rgba(0,0,0,0.62)", border: "2px solid rgb(245 158 11)", zIndex: 61, pointerEvents: "none", transition: "all .2s ease" }}
      />
      <div style={{ position: "fixed", width: CARD_W, zIndex: 62, ...cardStyle }} className="rounded-2xl border border-border/70 bg-card p-5 shadow-2xl">
        {CardInner}
      </div>
    </div>
  );
}
